import isObjectLike from "lodash/isObjectLike";

import {
  type MessagePathPart,
  CanonicalDataType,
  MessagePathPartType,
} from "@/shared/domain/topics";

import { DataExtractionError } from "./DataExtractionError";
import {
  type IndexedCollection,
  isIndexedCollection,
  isTypedArray,
} from "./isIndexedCollection";
import { resolveMessagePathKey } from "./resolveMessagePathKey";

type KeyedOrIndexedCollection = Record<string, unknown> | IndexedCollection;

/**
 * Extract a list of values from a message at the given path.
 * Supports picking values out of arrays, at any depth and amount of nesting.
 *
 * Attribute names are resolved against the decoded message via
 * `resolveMessagePathKey` (bridging ROS time `nanosec` -> `nsec`). Any
 * unresolved attribute -- intermediate or leaf -- throws `DataExtractionError`.
 * This strict contract differs from `valueAtMessagePath`, which returns
 * `undefined` for an unresolved leaf; both are intentional and caller-relied-on.
 */
export function valuesAtMessagePath(
  messageData: Record<string, unknown>,
  path: MessagePathPart[],
): unknown[] {
  if (path.length === 0) {
    return [];
  }
  const messagePath = [...path];

  let obj: KeyedOrIndexedCollection = messageData;
  while (messagePath.length > 0) {
    const part = messagePath.shift();
    if (!part) {
      break;
    }
    if (part.type === MessagePathPartType.Attr) {
      // Resolve the recorded attribute to the key the decoded message actually
      // carries (bridges ROS time `nanosec` -> `nsec`); undefined means absent.
      const key = resolveMessagePathKey(obj, part.attribute);
      if (
        typeof obj !== "object" ||
        obj === null ||
        isIndexedCollection(obj) ||
        key === undefined
      ) {
        throw DataExtractionError.fromMessagePath(path);
      }
      switch (part.dataType) {
        case CanonicalDataType.NumberArray: // fall through all array types
        case CanonicalDataType.Array: {
          const indexedCollection = obj[key] as KeyedOrIndexedCollection;
          if (!isIndexedCollection(indexedCollection)) {
            throw DataExtractionError.fromMessagePath(path);
          }
          obj = indexedCollection;
          break;
        }
        case CanonicalDataType.Object: {
          const keyedOrIndexedCollection = obj[key] as KeyedOrIndexedCollection;
          if (!isObjectLike(keyedOrIndexedCollection)) {
            throw DataExtractionError.fromMessagePath(path);
          }
          obj = keyedOrIndexedCollection;
          break;
        }
        default: {
          const value = obj[key];
          return [value];
        }
      }
    } else {
      if (!isIndexedCollection(obj)) {
        throw DataExtractionError.fromMessagePath(path);
      }
      const slice = obj.slice(part.start, part.end ?? Infinity);
      if (messagePath.length > 0) {
        if (isTypedArray(slice)) {
          // This check is here for exhaustiveness.
          // A TypedArray cannot hold anything other than numbers,
          // so it makes no sense to attempt to dive further into it.
          // But, TypedArray::flatMap isn't a thing, so, we check.
          throw DataExtractionError.fromMessagePath(path);
        }
        // If there are still message path segments to pick through,
        // fan out recursively to extract values for each item of the slice
        return slice.flatMap((item) => {
          return valuesAtMessagePath(item as Record<string, unknown>, [
            ...messagePath,
          ]);
        });
      } else {
        // Otherwise, just return the values in the slice
        return [...slice];
      }
    }
  }
  return [obj];
}
